import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Link, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import {
  DEFAULT_DROPBOX_ROOT,
  DROPBOX_FOLDER_STATUS,
} from 'dashboard409a/components/OperationsModal/dropbox.constants';
import {
  getDropboxFolderStatusConfig,
  stripDropboxRootPrefix,
} from 'dashboard409a/components/OperationsModal/dropbox.utils';
import WorkboardFolderPicker from 'dashboard409a/components/OperationsModal/WorkboardFolderPicker';
import { toWorkboardRelativePrefixFromStorageReference } from 'dashboard409a/components/OperationsModal/workboardPath.utils';
import ConsultantCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/ConsultantCell';
import EngagementManagerCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/EngagementManagerCell';
import { ConditionalLoader, Section } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import ValuationDealInfo from 'dashboard409a/pages/Operations/components/ValuationDealInfo/ValuationDealInfo';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import theme from 'theme';
import PersonnelRoleCard from './PersonnelRoleCard';
import ValuationStatusSelect from './ValuationStatusSelect';
import { getFinancialFields } from './ValuedInformation.constants';
import { ValuedInformationProps } from './ValuedInformation.props';
import {
  Container,
  Field,
  FinancialDateBadge,
  FinancialField,
  FinancialFieldHeader,
  FinancialFieldsGrid,
  HeaderRow,
  Label,
  PersonnelRow,
  StatusBadge,
  StyledLinkSpan,
  UserSection,
  Value,
} from './ValuedInformation.styles';

interface OtherConsultantsFieldProps {
  consultants?: { name: string; imageSrc: string }[];
  fallbackName?: string;
  fallbackImageSrc?: string;
  isLoading?: boolean;
}

const OtherConsultantsField = ({
  consultants,
  fallbackName,
  fallbackImageSrc,
  isLoading,
}: OtherConsultantsFieldProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  let list: { name: string; imageSrc: string }[];
  if (consultants && consultants.length > 0) {
    list = consultants;
  } else if (fallbackName && fallbackName !== '-') {
    list = [{ name: fallbackName, imageSrc: fallbackImageSrc || '' }];
  } else {
    list = [];
  }
  const first = list[0];
  const rest = list.slice(1);

  if (!first) {
    return (
      <UserSection>
        <Avatar data={[{ picture_url: '' }]} name="-" isLoading={isLoading} />
      </UserSection>
    );
  }

  return (
    <UserSection>
      <Avatar data={[{ picture_url: first.imageSrc }]} name={first.name} isLoading={isLoading} />
      {rest.length > 0 && (
        <>
          <Chip
            label={`+${rest.length}`}
            size="small"
            onClick={e => setAnchorEl(e.currentTarget)}
            sx={{ cursor: 'pointer', ml: 0.5 }}
          />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            <Stack spacing={1} sx={{ p: 1.5 }}>
              {rest.map(c => (
                <Avatar key={c.name} data={[{ picture_url: c.imageSrc }]} name={c.name} />
              ))}
            </Stack>
          </Popover>
        </>
      )}
    </UserSection>
  );
};

const ValuedInformation = ({
  isFromModels,
  valuedInformation,
  deal,
  onDealStageChange,
  onSendFinalInvoice,
  isLoading,
  statusOptions,
  currentStatusId,
  onStatusChange,
  isStatusLoading = false,
  companyFolder,
  valuationFolder,
  dropboxRoot = DEFAULT_DROPBOX_ROOT,
  valuationFolderStatus = 'idle',
  valuationFolderSimilarMatchesCount = 0,
  suggestedValuationFolderName,
  onUpdateValuationFolder,
  onEngagementManagerUpdate,
  onOtherConsultantsUpdate,
  onBrowseDocuments,
  isBrowseDocumentsDisabled = false,
  uploadedDocumentCount = null,
  onProcessReport,
  isProcessReportDisabled = false,
  onOnboardingEmail,
  isOnboardingEmailDisabled = false,
  pendingRequestedCount = null,
}: ValuedInformationProps) => {
  const engagementManagerRow = useMemo((): SimplifiedAttioValuation | null => {
    if (!valuedInformation?.valuationId || !onEngagementManagerUpdate) {
      return null;
    }
    return {
      record_id: valuedInformation.valuationId,
      deal_value: null,
      company_name: null,
      status: null,
      valuation_date: null,
      valuation_fee: null,
      engagement_manager: valuedInformation.valuationEngagementManager || null,
      engagement_manager_id: valuedInformation.valuationEngagementManagerId || null,
      engagement_manager_image_src: valuedInformation.valuationEngagementManagerImageSrc || null,
      other_consultant: null,
      other_consultant_image_src: null,
      deal: null,
      deal_due: null,
      deal_stage: null,
      created_at: '',
      is_loading_additional_data: false,
    };
  }, [valuedInformation, onEngagementManagerUpdate]);

  const consultantRow = useMemo((): SimplifiedAttioValuation | null => {
    if (!valuedInformation?.valuationId || !onOtherConsultantsUpdate) {
      return null;
    }

    const {
      valuationId,
      otherConsultants,
      otherConsultantIds,
      otherConsultantName: rawOtherConsultantName,
      otherConsultantImageSrc: rawOtherConsultantImageSrc,
      otherConsultantId,
    } = valuedInformation;
    const hasOtherConsultantsList = Boolean(otherConsultants && otherConsultants.length > 0);
    const otherConsultantIdsFromList = otherConsultants
      ?.map(c => c.targetRecordId)
      .filter((id): id is string => Boolean(id));
    const otherConsultantName =
      rawOtherConsultantName && rawOtherConsultantName !== '-' ? rawOtherConsultantName : null;
    const otherConsultantImageSrc =
      rawOtherConsultantImageSrc && rawOtherConsultantImageSrc !== '-' ? rawOtherConsultantImageSrc : null;

    let otherConsultantIdField: string[] | string | null = otherConsultantId || null;
    if (otherConsultantIdsFromList && otherConsultantIdsFromList.length > 0) {
      otherConsultantIdField = otherConsultantIdsFromList;
    } else if (otherConsultantIds && otherConsultantIds.length > 0) {
      otherConsultantIdField = otherConsultantIds;
    }

    return {
      record_id: valuationId,
      deal_value: null,
      company_name: null,
      status: null,
      valuation_date: null,
      valuation_fee: null,
      engagement_manager: null,
      engagement_manager_image_src: null,
      other_consultant: hasOtherConsultantsList
        ? (otherConsultants!.map(c => c.name) as unknown as string)
        : otherConsultantName,
      other_consultant_id: otherConsultantIdField as unknown as string,
      other_consultant_image_src: hasOtherConsultantsList
        ? (otherConsultants!.map(c => c.imageSrc) as unknown as string)
        : otherConsultantImageSrc,
      deal: null,
      deal_due: null,
      deal_stage: null,
      created_at: '',
      is_loading_additional_data: false,
    };
  }, [valuedInformation, onOtherConsultantsUpdate]);
  const [valuationFolderAnchorEl, setValuationFolderAnchorEl] = useState<HTMLElement | null>(null);
  const [valuationFolderValue, setValuationFolderValue] = useState(valuationFolder || '');
  const [isSavingValuationFolder, setIsSavingValuationFolder] = useState(false);
  const [selectedValuationFolderPath, setSelectedValuationFolderPath] = useState<string | null>(null);
  const isValuationFolderPopoverOpen = Boolean(valuationFolderAnchorEl);

  const companyFolderPrefix = useMemo(
    () => toWorkboardRelativePrefixFromStorageReference(companyFolder, dropboxRoot),
    [companyFolder, dropboxRoot]
  );

  const resolveValuationFolderPath = useCallback(
    (folderRef?: string | null) => {
      if (!folderRef?.trim()) {
        return null;
      }
      if (folderRef.includes('/')) {
        return folderRef.endsWith('/') ? folderRef : `${folderRef}/`;
      }
      return companyFolderPrefix ? `${companyFolderPrefix}${folderRef}/` : `${folderRef}/`;
    },
    [companyFolderPrefix]
  );

  // Update local state when valuationFolder prop changes
  useEffect(() => {
    if (valuationFolder !== undefined) {
      setValuationFolderValue(stripDropboxRootPrefix(valuationFolder || '', [dropboxRoot]));
      setSelectedValuationFolderPath(resolveValuationFolderPath(valuationFolder));
    }
  }, [companyFolder, dropboxRoot, resolveValuationFolderPath, valuationFolder]);

  const handleBrowseDocuments = () => {
    if (!onBrowseDocuments) {
      return;
    }

    onBrowseDocuments();
  };

  const resetValuationFolderDraft = useCallback(() => {
    setValuationFolderValue(stripDropboxRootPrefix(valuationFolder || '', [dropboxRoot]));
    setSelectedValuationFolderPath(resolveValuationFolderPath(valuationFolder));
  }, [dropboxRoot, resolveValuationFolderPath, valuationFolder]);

  const closeValuationFolderPopover = useCallback(() => {
    setValuationFolderAnchorEl(null);
    resetValuationFolderDraft();
  }, [resetValuationFolderDraft]);

  const openValuationFolderPopover = (anchorEl: HTMLElement) => {
    if (!onUpdateValuationFolder || !companyFolder?.trim()) {
      return;
    }
    setValuationFolderValue(
      stripDropboxRootPrefix(valuationFolder || '', [dropboxRoot]) || suggestedValuationFolderName || ''
    );
    setSelectedValuationFolderPath(resolveValuationFolderPath(valuationFolder));
    setValuationFolderAnchorEl(anchorEl);
  };

  const handleValuationFolderSync = async () => {
    if (!onUpdateValuationFolder || !selectedValuationFolderPath) {
      return;
    }

    const folderNameOnly = valuationFolderValue.trim();

    if (folderNameOnly === (valuationFolder || '')) {
      closeValuationFolderPopover();
      return;
    }

    setIsSavingValuationFolder(true);
    try {
      await onUpdateValuationFolder(folderNameOnly, selectedValuationFolderPath);
      setValuationFolderAnchorEl(null);
    } catch {
      // Error is handled by the parent component
    } finally {
      setIsSavingValuationFolder(false);
    }
  };

  const handleValuationFolderSelect = (directory: { name: string; path: string } | null) => {
    if (!directory) {
      setValuationFolderValue('');
      setSelectedValuationFolderPath(null);
      return;
    }
    setValuationFolderValue(directory.name);
    setSelectedValuationFolderPath(directory.path.endsWith('/') ? directory.path : `${directory.path}/`);
  };

  const statusConfig = getDropboxFolderStatusConfig(valuationFolderStatus);
  let valuationStatusLabel = statusConfig.label;
  if (valuationFolderStatus === 'searching') {
    valuationStatusLabel = 'Listing folders...';
  } else if (valuationFolderStatus === 'matches') {
    valuationStatusLabel = 'Folders Loaded';
  } else if (valuationFolderStatus === 'not_found') {
    valuationStatusLabel = 'No Folders Found';
  }

  const allFields = getFinancialFields(valuedInformation);

  const hasFinancialFieldValue = (value: string | number | undefined | null) => {
    if (value === undefined || value === null || value === '') {
      return false;
    }
    if (value === '-') {
      return false;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) && value !== 0;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed === '-') {
      return false;
    }
    const numericValue = Number(trimmed.replace(/[$,]/g, ''));
    if (Number.isFinite(numericValue) && numericValue === 0) {
      return false;
    }
    return true;
  };

  const visibleFields = allFields.filter(field => hasFinancialFieldValue(field.getValue()));

  const isValuationActionsDisabled = !companyFolder?.trim();
  const displayValuationFolderPath =
    companyFolderPrefix && valuationFolder
      ? `${stripDropboxRootPrefix(companyFolderPrefix, [dropboxRoot])}/${valuationFolder}`
      : valuationFolder || '';

  const getValuationFolderPath = (folderValue: string) => {
    if (companyFolderPrefix && folderValue) {
      return `${dropboxRoot}/${stripDropboxRootPrefix(companyFolderPrefix, [dropboxRoot])}/${folderValue}`;
    }
    if (folderValue) {
      return `${dropboxRoot}/${folderValue}`;
    }
    return `${dropboxRoot}/`;
  };

  const valuationFolderContent = (
    <Tooltip
      title={
        isValuationActionsDisabled
          ? 'Select and sync a Company Folder for this company before listing or syncing the valuation folder.'
          : ''
      }>
      <Typography
        onClick={event => openValuationFolderPopover(event.currentTarget)}
        sx={{
          width: 'fit-content',
          cursor: onUpdateValuationFolder ? 'pointer' : 'default',
          color: onUpdateValuationFolder ? 'primary.main' : 'text.primary',
          fontStyle: 'italic',
          '&:hover': onUpdateValuationFolder ? { textDecoration: 'underline' } : {},
        }}>
        {displayValuationFolderPath || 'Click here to set the folder path'}
      </Typography>
    </Tooltip>
  );

  const valuationFolderPopover = (
    <Popover
      open={isValuationFolderPopoverOpen}
      anchorEl={valuationFolderAnchorEl}
      onClose={closeValuationFolderPopover}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
      <Stack spacing={1.5} sx={{ p: 2, width: 360, maxWidth: '90vw' }}>
        <TextField
          label="Company Folder (inherited)"
          value={stripDropboxRootPrefix(companyFolderPrefix, [dropboxRoot]) || `${dropboxRoot}/`}
          size="small"
          fullWidth
          disabled
        />
        <WorkboardFolderPicker
          initialPrefix={companyFolderPrefix}
          dropboxRoot={dropboxRoot}
          companyName={suggestedValuationFolderName}
          selectedPath={selectedValuationFolderPath}
          onSelect={handleValuationFolderSelect}
        />
        <Typography variant="caption" color="text.secondary">
          {`Path: ${getValuationFolderPath(valuationFolderValue || '<folder>')}`}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            onClick={handleValuationFolderSync}
            disabled={
              !selectedValuationFolderPath ||
              !valuationFolderValue.trim() ||
              isSavingValuationFolder ||
              valuationFolderStatus === 'syncing'
            }>
            Save
          </Button>
          <Button variant="text" size="small" onClick={closeValuationFolderPopover}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );

  return (
    <Section
      title="Valuation"
      headerAction={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <ConditionalLoader isLoading={isLoading && !valuedInformation?.bookingType} width={150}>
              <StatusBadge>{valuedInformation?.bookingType || '-'}</StatusBadge>
            </ConditionalLoader>
            {valuedInformation?.valuationNotice && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#CDE5FA',
                  padding: 1,
                  borderRadius: 1,
                }}>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ fontSize: '0.875rem', color: theme.palette.primary.main }}>
                  NOTICE:
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ fontSize: '0.875rem', color: theme.palette.primary.main }}>
                  {valuedInformation.valuationNotice}
                </Typography>
              </Box>
            )}
          </Box>
          <ValuationStatusSelect
            statusOptions={statusOptions || []}
            currentStatusId={currentStatusId}
            onStatusChange={onStatusChange}
            isLoading={isStatusLoading}
          />
        </Box>
      }>
      <Container>
        <Grid size={{ md: 8, sm: 12 }}>
          <HeaderRow>
            <Field>
              <ConditionalLoader isLoading={isLoading && !valuedInformation?.valuationName} width={150}>
                <Link href={valuedInformation?.valuationUrl} target="_blank" rel="noopener noreferrer">
                  <StyledLinkSpan>{valuedInformation?.valuationName || '-'}</StyledLinkSpan>
                </Link>
              </ConditionalLoader>
            </Field>
            <Field sx={{ mt: 2, width: 'fit-content' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Valuation Folder:
                </Typography>
                <ConditionalLoader isLoading={isLoading} width={200}>
                  {valuationFolderContent}
                </ConditionalLoader>
                {valuationFolderPopover}
                {valuationFolderStatus !== DROPBOX_FOLDER_STATUS.IDLE && (
                  <Chip size="small" color={statusConfig.color} label={valuationStatusLabel} />
                )}
                {onBrowseDocuments && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleBrowseDocuments}
                    disabled={isBrowseDocumentsDisabled}
                    id="operations-browse-documents-button"
                    data-testid="operations-browse-documents-button">
                    Browse Documents
                    {uploadedDocumentCount === null ? '' : ` (uploaded: ${uploadedDocumentCount})`}
                  </Button>
                )}
                {onProcessReport && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onProcessReport}
                    disabled={isProcessReportDisabled}
                    id="process-report-button">
                    Process Report
                  </Button>
                )}
                {onOnboardingEmail && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onOnboardingEmail}
                    disabled={isOnboardingEmailDisabled}
                    id="onboarding-email-button">
                    Onboarding Email
                    {pendingRequestedCount === null ? '' : ` (pending: ${pendingRequestedCount})`}
                  </Button>
                )}
                {valuationFolderSimilarMatchesCount > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {valuationFolderSimilarMatchesCount}{' '}
                    {valuationFolderSimilarMatchesCount === 1 ? 'folder' : 'folders'} found in the company folder.
                    Select the correct folder from the tree.
                  </Typography>
                )}
              </Box>
            </Field>
          </HeaderRow>
          <Grid container spacing={2}>
            <Grid size={{ md: 8, sm: 12 }}>
              <PersonnelRow>
                <PersonnelRoleCard
                  label="Valuation Lead"
                  name={valuedInformation?.valuationLeadName || '-'}
                  imageSrc={valuedInformation?.valuationLeadImageSrc || ''}
                  isLoading={isLoading && !valuedInformation?.valuationLeadName}
                />
                {engagementManagerRow ? (
                  <PersonnelRoleCard label="Engagement Manager">
                    <UserSection>
                      <EngagementManagerCell
                        valuationRow={engagementManagerRow}
                        onEngagementManagerUpdate={onEngagementManagerUpdate}
                      />
                    </UserSection>
                  </PersonnelRoleCard>
                ) : (
                  <PersonnelRoleCard
                    label="Engagement Manager"
                    name={valuedInformation?.valuationEngagementManager || '-'}
                    imageSrc={valuedInformation?.valuationEngagementManagerImageSrc || ''}
                    isLoading={isLoading && !valuedInformation?.valuationEngagementManager}
                  />
                )}
                {consultantRow ? (
                  <PersonnelRoleCard label="Other Consultant(s)">
                    <UserSection>
                      <ConsultantCell
                        valuationRow={consultantRow}
                        onOtherConsultantsUpdate={onOtherConsultantsUpdate}
                      />
                    </UserSection>
                  </PersonnelRoleCard>
                ) : (
                  <PersonnelRoleCard label="Other Consultant(s)">
                    <OtherConsultantsField
                      consultants={valuedInformation?.otherConsultants}
                      fallbackName={valuedInformation?.otherConsultantName}
                      fallbackImageSrc={valuedInformation?.otherConsultantImageSrc}
                      isLoading={isLoading && !valuedInformation?.otherConsultantName}
                    />
                  </PersonnelRoleCard>
                )}
              </PersonnelRow>
              <Box mt={0.5} />
              <FinancialFieldsGrid>
                {visibleFields.map(field => {
                  const value = field.getValue();
                  const dateValue = field.getDateValue?.();
                  let displayValue: string = '-';
                  if (field.isCurrency) {
                    if (value !== undefined && value !== null && value !== '-') {
                      displayValue = formatCurrency({
                        value: typeof value === 'number' ? value : Number(value) || 0,
                      });
                    }
                  } else {
                    displayValue = (value as string) || '-';
                  }
                  return (
                    <FinancialField key={field.key}>
                      <FinancialFieldHeader>
                        <Label sx={{ fontWeight: 400, textWrap: 'nowrap' }}>{field.label}</Label>
                        {hasFinancialFieldValue(dateValue) && <FinancialDateBadge>{dateValue}</FinancialDateBadge>}
                      </FinancialFieldHeader>
                      <ConditionalLoader isLoading={isLoading && !valuedInformation} width={150}>
                        <Value sx={{ fontSize: '1.125rem', fontWeight: 700 }}>{displayValue}</Value>
                      </ConditionalLoader>
                    </FinancialField>
                  );
                })}
              </FinancialFieldsGrid>
            </Grid>
            <Grid size={{ md: 4, sm: 12 }}>
              {onDealStageChange && onSendFinalInvoice && (
                <ValuationDealInfo
                  deal={deal}
                  isLoading={isLoading}
                  onDealStageChange={onDealStageChange}
                  onSendFinalInvoice={onSendFinalInvoice}
                  isFromModels={isFromModels}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Section>
  );
};

export default ValuedInformation;
